import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Sparkles, Zap, LineChart as LineChartIcon, Brain,
  Image as ImageIcon, Search, Target, BarChart3, Globe2, Wand2,
  Layers, ShoppingBag, Star, ChevronDown, Check, ArrowRight,
  CalendarClock,
} from "lucide-react";
import MarketingShell from "../components/marketing/MarketingShell";
import MyntraPartnerBadge from "../components/marketing/MyntraPartnerBadge";
import ETInnovatorBadge from "../components/marketing/ETInnovatorBadge";
import HeroCounterStrip from "../components/marketing/HeroCounterStrip";
import DemoModal from "../components/marketing/DemoModal";
import DashboardPreview from "../components/marketing/DashboardPreview";
import { POSTS } from "../blog/posts";

// Public CTAs use a quotation-based flow — no public pricing or QR codes.
const QUOTATION_WHATSAPP =
  "https://wa.me/919464645491?text=" +
  encodeURIComponent(
    "Hi BrandBorn — I'd like a quotation for the AI listings plan. Please share details."
  );
const QUOTATION_EMAIL =
  "mailto:info@ecomcatalog.ai?subject=" +
  encodeURIComponent("Quotation Request — BrandBorn AI Plans");

const MARKETPLACES = [
  { name: "Myntra",   color: "#F43397", domain: "myntra.com" },
  { name: "Meesho",   color: "#570D48", domain: "meesho.com" },
  { name: "Nykaa",    color: "#E5097F", domain: "nykaa.com" },
  { name: "Ajio",     color: "#8B5CF6", domain: "ajio.com" },
  { name: "Amazon",   color: "#FF9900", domain: "amazon.in" },
  { name: "Flipkart", color: "#2874F0", domain: "flipkart.com" },
  { name: "Swiggy",   color: "#FC8019", domain: "swiggy.com" },
  { name: "Blinkit",  color: "#F8CB46", domain: "blinkit.com" },
];

const FEATURES = [
  { icon: Sparkles,   title: "AI Auto Listing",         body: "Drop one photo. Claude Vision auto-fills 89 columns of brand, fabric, HSN, neck, sleeve & more in 90 seconds." },
  { icon: Wand2,      title: "AI Title & Description",  body: "SEO-tuned titles (≤90 chars), 200-char descriptions, and 6-10 keyword tags written for each marketplace." },
  { icon: Layers,     title: "Bulk Listing",            body: "Upload a folder of SKUs. Multi-product queue stacks them and ships one marketplace-ready Excel." },
  { icon: Globe2,     title: "Marketplace Integrations", body: "Myntra · Nykaa · Ajio · Amazon · Flipkart · Swiggy Instamart · Blinkit — one schema, every export." },
  { icon: Target,     title: "Ad Optimization",          body: "SCALE / TEST / AVOID buckets for every SKU. ROAS targets and bid suggestions powered by your real data." },
  { icon: Search,     title: "Competitor Analysis",      body: "Track rival SKUs by URL. Price-delta alerts on email whenever competitors move ±5%." },
  { icon: LineChartIcon, title: "Product Trend Analysis", body: "What's surging this week vs last. Category-level demand curves so you launch what's hot." },
  { icon: Brain,      title: "Brand Growth Insights",    body: "Ranked action items by impact × effort — reshoot images, fix titles, rebalance pricing bands." },
  { icon: Zap,        title: "SEO Optimization",         body: "On-page audit, 90-char title checker, keyword density, and AEO/GEO schema generation built-in." },
  { icon: BarChart3,  title: "Smart Analytics Dashboard", body: "Unified GMV, ROAS, RTO and conversion across every channel — beautiful, instant, exportable." },
];

const TESTIMONIALS = [
  { quote: "Cut our cataloguing time from 3 days to 3 hours. We list 200 SKUs every Monday morning now.", name: "Priya Sharma", role: "Founder, Anouk Kurtis" },
  { quote: "The new RTO + cancellation drill-down saved us ₹80,000/month by surfacing the SKUs to fix.", name: "Rohit Bhatia", role: "Head of E-commerce, Loft" },
  { quote: "First tool that actually understands Myntra's column maze. Listings approve 96% first-time.", name: "Anjali Mehra", role: "D2C Lead, BeeYou Beauty" },
  { quote: "Competitor sniper saved us from a pricing race-to-the-bottom on Blinkit. ₹14L recovered Q1.", name: "Karan Aggarwal", role: "Co-founder, SnackNation" },
  { quote: "We replaced our entire ₹1.2L/month listing agency with this. Pays for itself in week one.", name: "Sneha Patel", role: "CEO, Wildcraft Mini" },
  { quote: "Reports went from a 5-day agency turnaround to 60 seconds. That alone was worth the switch.", name: "Aditya Iyer", role: "Growth, Kapas Kids" },
];

const PLANS = [
  {
    id: "starter_100",
    tier: "Starter",
    tag: "Get started",
    price: 2499,
    listings: 100,
    perListing: 24.99,
    desc: "For new brands shipping their first batch of SKUs.",
    features: [
      "100 AI listings",
      "Strict Myntra invariants formatting",
      "Marketplace-ready Excel export",
      "Image hash caching (70% LLM savings)",
      "Email support",
    ],
    cta: "Buy Starter",
    highlight: false,
  },
  {
    id: "pro_500",
    tier: "Pro",
    tag: "Most popular",
    price: 4999,
    listings: 500,
    perListing: 9.998,
    desc: "Sweet spot for growing D2C brands. Best price-per-listing.",
    features: [
      "500 AI listings",
      "Data Analysis dashboard",
      "Brand Growth tab",
      "Ads Suggestions (AI scale / test / avoid)",
      "Monthly Sales Report data",
      "Competitor Analysis (Myntra-only)",
      "Priority WhatsApp support",
    ],
    cta: "Buy Pro",
    highlight: true,
  },
  {
    id: "business_1000",
    tier: "Business",
    tag: "Full access",
    price: 8999,
    listings: 1000,
    perListing: 8.999,
    desc: "Everything in Pro plus the full workspace for scaling brands.",
    features: [
      "1000 AI listings · lowest cost / listing",
      "Everything in Pro",
      "My Catalogue workspace",
      "Dedicated success manager",
    ],
    cta: "Buy Business",
    highlight: false,
  },
];

const FAQ = [
  { q: "What is EcomCatalog AI?", a: "An AI catalog generator and ecommerce data analytics platform for Indian D2C sellers across Myntra, Nykaa, Ajio, Amazon, Flipkart, Swiggy Instamart and Blinkit." },
  { q: "How does the 90-second AI listing work?", a: "Upload one product photo. Claude Sonnet 4.5 Vision detects category, fills brand, fabric, HSN, neck, sleeve, occasion, SEO title and description automatically. You pick sizes, click Generate, and a marketplace-ready Excel downloads in ~90 seconds." },
  { q: "Which marketplaces are supported?", a: "Myntra and Meesho with native ready-to-upload Excel exports, plus Nykaa, Ajio, Amazon India, Flipkart, Swiggy Instamart and Blinkit catalog support." },
  { q: "Do I still need a listing agency?", a: "No. The Vision AI auto-listing + analytics layer replaces the typical agency workflow end-to-end. Brands save ₹40,000–₹2,00,000/month on retainers." },
  { q: "Is there a trial?", a: "Yes — sign up free with just your email and mobile number and get 1 AI listing free, no credit card needed. Use it to feel the workflow, then connect with us on WhatsApp or email for a custom quotation tailored to your catalog size." },
  { q: "Can it work for quick commerce (Blinkit, Instamart)?", a: "Absolutely. We export quick-commerce-formatted catalogs and analyse Blinkit and Swiggy Instamart performance alongside Myntra in one dashboard." },
];

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-[11px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
      {children}
    </span>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div
      className="mkt-glass mkt-hover-lift p-6 group"
      data-testid={`feat-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-400/30 grid place-items-center mb-4">
        <Icon size={18} className="text-indigo-300" />
      </div>
      <div className="font-mkt-display text-lg font-semibold tracking-tight text-white">{title}</div>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-white/[0.06] py-5 cursor-pointer"
      onClick={() => setOpen((o) => !o)}
      data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-mkt-display text-lg font-semibold text-white tracking-tight">{q}</h3>
        <ChevronDown
          size={18}
          className={`text-slate-400 transition-transform duration-300 ${open ? "rotate-180 text-indigo-300" : ""}`}
        />
      </div>
      {open && <p className="text-sm text-slate-400 mt-3 leading-relaxed max-w-3xl">{a}</p>}
    </div>
  );
}

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  const previewPosts = POSTS.slice(0, 3);

  return (
    <MarketingShell>
      {/* ═══════════ 1 · HERO ═══════════ */}
      <section className="relative pt-12 sm:pt-20 pb-24">
        <div className="absolute inset-x-0 top-0 h-[600px] mkt-grid-bg opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mkt-glass text-[11px] uppercase tracking-[0.25em] text-slate-300 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mkt-pulse-dot" />
              AI for Myntra, Meesho, Nykaa, Ajio, Amazon and More
            </div>

            <h1
              className="font-mkt-display text-4xl sm:text-5xl md:text-6xl lg:text-[68px] font-semibold leading-[1.05] tracking-tighter mt-6"
              data-testid="hero-headline"
            >
              <span className="inline-block bg-gradient-to-r from-[#FF9933] via-white to-[#138808] bg-clip-text text-transparent">
                India&apos;s First
              </span>{" "}
              <span className="mkt-gradient-text-vivid">AI auto-listings</span>{" "}
              &amp;{" "}
              <span className="mkt-gradient-text-vivid">data-analysis</span>{" "}
              software for ecommerce sellers.
            </h1>
            <p className="text-lg text-slate-400 mt-6 max-w-2xl leading-relaxed">
              EcomCatalog AI streamlines your ecommerce operations — from creating
              product listings in just 5 clicks to AI-powered ad planning, order
              and cancellation reports, and actionable brand-growth strategies
              across every Indian marketplace and quick-commerce platform.
            </p>
            {/* Primary CTAs — all three side-by-side for a clean, aligned row */}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="mkt-btn-gradient" data-testid="hero-start">
                Start Free Trial <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/pricing#pricing"
                className="mkt-btn-ghost"
                data-testid="hero-take-monthly"
              >
                Take Monthly Plan
              </Link>
              <button
                type="button"
                onClick={() => setDemoOpen(true)}
                data-testid="hero-book-demo"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-indigo-400/40 bg-indigo-500/[0.08] hover:bg-indigo-500/[0.16] text-sm font-semibold text-indigo-100 hover:text-white transition-all"
              >
                <CalendarClock size={15} className="text-indigo-300" />
                Book a Demo
                <ArrowRight size={14} />
              </button>
            </div>

            {/* Feature pills + partner badge share one row so nothing feels orphaned */}
            <div className="mt-7 flex flex-wrap items-center gap-2.5">
              <Pill>1 free AI listing</Pill>
              <Pill>Myntra + Meesho Excel</Pill>
              <Pill>225+ Myntra categories</Pill>
              <Pill>7 marketplaces</Pill>
              <Pill>90-sec listings</Pill>
              <Pill>Made in India</Pill>
              <MyntraPartnerBadge variant="hero" />
            </div>

            {/* Economic Times AI Innovator nomination — highlighted */}
            <div className="mt-5">
              <ETInnovatorBadge variant="hero" />
            </div>

            {/* Live traction — animated counters (also read by LLM crawlers for citation) */}
            <HeroCounterStrip />
          </div>
        </div>
      </section>

      {/* ═══════════ 4 · MARKETPLACE STRIP (marquee) ═══════════ */}
      <section className="py-12 border-y border-white/[0.05]" data-testid="marketplaces-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center text-[11px] uppercase tracking-[0.3em] text-slate-500 font-bold mb-7">
            Integrated with India's biggest marketplaces &amp; q-commerce apps
          </div>
          <div className="overflow-hidden mask-fade">
            <div className="flex gap-4 mkt-marquee">
              {[...MARKETPLACES, ...MARKETPLACES, ...MARKETPLACES].map((m, i) => (
                <div
                  key={i}
                  className="shrink-0 px-7 py-4 rounded-2xl mkt-glass flex items-center justify-center gap-3 min-w-[160px]"
                >
                  <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={`/logos/${m.name.toLowerCase()}.png`}
                      alt={`${m.name} logo`}
                      className="w-7 h-7 object-contain"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                    />
                  </span>
                  <span
                    className="font-mkt-display font-semibold text-lg tracking-tight"
                    style={{ color: m.color }}
                  >
                    {m.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ DEMO VIDEO ═══════════ */}
      <section className="py-24" data-testid="demo-video-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Watch it work
            </div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
              See a listing built in{" "}
              <span className="mkt-gradient-text-vivid">90 seconds.</span>
            </h2>
            <p className="text-base text-slate-400 mt-4 max-w-xl mx-auto">
              One photo in, a Myntra-ready Excel out. Watch the full demo —
              then try it yourself with 1 free listing.
            </p>
          </div>
          <div className="mkt-glass overflow-hidden rounded-3xl p-2">
            <div className="aspect-video rounded-2xl overflow-hidden bg-black">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/g8iFk0UvSls"
                title="EcomCatalog AI — full product demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="demo-video-iframe"
              />
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/signup" className="mkt-btn-gradient" data-testid="demo-video-cta">
              Try it free — 1 listing <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ PRICING (moved right under demo per user feedback) ═══════════ */}
      <section id="pricing" className="py-24" data-testid="pricing">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Plans
            </div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
              Pick the plan that fits your <span className="mkt-gradient-text-vivid">brand.</span>
            </h2>
            <p className="text-base text-slate-400 mt-4">
              Custom quotation tailored to your catalog size and marketplaces. Connect with us
              on WhatsApp or email — we&apos;ll share a personalised plan within minutes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`relative p-7 rounded-3xl border ${
                  p.highlight
                    ? "border-indigo-400/40 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-transparent shadow-[0_20px_60px_-20px_rgba(124,58,237,0.55)]"
                    : "border-white/[0.08] bg-white/[0.02]"
                }`}
                data-testid={`pricing-${p.id}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-[10px] uppercase tracking-[0.2em] font-bold text-white">
                    {p.tag}
                  </div>
                )}
                {!p.highlight && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                    {p.tag}
                  </div>
                )}
                <div className="font-mkt-display text-3xl font-semibold mt-2">{p.tier}</div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
                  {p.listings} AI listings · custom quote
                </div>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed min-h-[44px]">
                  {p.desc}
                </p>
                <a
                  href={QUOTATION_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-5 inline-flex w-full justify-center items-center gap-2 ${
                    p.highlight ? "mkt-btn-gradient" : "mkt-btn-ghost"
                  }`}
                  data-testid={`pricing-cta-${p.id}`}
                >
                  Connect for Quotation <ArrowUpRight size={14} />
                </a>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check size={15} className="text-indigo-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center text-xs text-slate-500">
            All plans · Credits never expire · GST exclusive · WhatsApp +91 94646 45491 · info@ecomcatalog.ai
          </div>

          {/* Onboard Your Brand — secondary CTA below the plan cards */}
          <div className="mt-10 flex flex-col items-center gap-3" data-testid="onboard-brand-cta-wrap">
            <a
              href="https://www.brandborn.in"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white overflow-hidden transition-all hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #ec4899 55%, #7c3aed 100%)",
                boxShadow: "0 18px 42px -12px rgba(236,72,153,0.55), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
              data-testid="onboard-brand-btn"
            >
              <Sparkles size={15} className="text-yellow-100" />
              Onboard Your Brand
              <ArrowUpRight size={15} />
              <span className="absolute inset-0 -z-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "linear-gradient(135deg, #ec4899 0%, #7c3aed 55%, #06b6d4 100%)" }}
              />
            </a>
            <p className="text-xs text-slate-500">
              Get onboarded across Myntra, Nykaa, Ajio, Amazon, Flipkart, Tata CLiQ, Blinkit & more →{" "}
              <span className="text-indigo-300 font-mono">brandborn.in</span>
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════ 3 · FEATURES GRID ═══════════ */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Capabilities
            </div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3 mkt-gradient-text">
              One workspace for everything an ecommerce brand needs.
            </h2>
            <p className="text-base text-slate-400 mt-4">
              From the first photograph to the final ROAS report — AI handles 80% of the
              grunt work, so your team ships drops instead of fighting spreadsheets.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 5 · ANALYTICS + CHARTS SHOWCASE ═══════════ */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
                Smart Analytics
              </div>
              <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
                Clarity on your <span className="mkt-gradient-text-vivid">brand data</span>, instantly.
              </h2>
              <p className="text-base text-slate-400 mt-5 leading-relaxed">
                Unify Myntra, Nykaa, Ajio, Amazon, Flipkart and quick-commerce performance
                into one live dashboard. AI calls out which SKUs to scale, which to fix and
                which to retire — backed by your real order data.
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  "Revenue, ROAS, RTO and conversion in one view",
                  "Per-SKU AI commands: Scale / Stop ads / Improve images",
                  "Cancellation reason mining + fix recommendations",
                  "Trend analysis: what's surging this week vs last",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check size={16} className="text-indigo-400 mt-0.5 shrink-0" /> {x}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features" className="mkt-btn-gradient" data-testid="analytics-learn-more">
                  Explore features <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <DashboardPreview compact />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 6 · TESTIMONIALS ═══════════ */}
      <section className="py-24" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Loved by D2C founders
            </div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
              Brands shipping faster with EcomCatalog AI.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="mkt-glass mkt-hover-lift p-6"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={13} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="font-mkt-display text-base text-slate-200 leading-relaxed">
                  "{t.quote}"
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 grid place-items-center text-sm font-semibold text-white">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 7 · PRICING (removed — moved up right under demo video) ═══════════ */}

      {/* ═══════════ 8 · FAQ ═══════════ */}
      <section className="py-24" id="faq" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Frequently asked
            </div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
              People also ask
            </h2>
          </div>
          <div className="mkt-glass p-2 sm:p-6">
            {FAQ.map((f) => (
              <Faq key={f.q} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 8b · BRANDSBYINDIA ═══════════ */}
      <section className="py-24 relative" data-testid="brandsbyindia-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="bg-gradient-to-br from-indigo-500/10 via-transparent to-fuchsia-500/10 border border-indigo-400/20 rounded-3xl p-8 sm:p-12">
            <div className="grid lg:grid-cols-[1fr,320px] gap-8 items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
                  New · BrandsByIndia
                </div>
                <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
                  Get your brand found on <span className="mkt-gradient-text-vivid">Google, ChatGPT &amp; Gemini.</span>
                </h2>
                <p className="text-slate-400 mt-5 leading-relaxed">
                  BrandsByIndia is our brand-visibility platform: every Indian brand gets a verified
                  public page, monthly AI-written SEO articles, and structured data that search engines
                  and AI assistants can read and cite. You manage everything with a simple ID &amp;
                  password — views, customer enquiries and growth. ₹999 per box/month (incl. GST),
                  auto-pay, cancel anytime.
                </p>
                <Link to="/brands" className="mkt-btn-gradient text-sm inline-flex mt-6" data-testid="landing-bbi-cta">
                  Explore BrandsByIndia <ArrowRight size={14} />
                </Link>
              </div>
              <img src="/bbi/growth.jpg" alt="BrandsByIndia — brand visibility growth on Google and AI search" loading="lazy"
                className="hidden lg:block w-full rounded-2xl border border-white/10 shadow-xl shadow-fuchsia-500/10"
                data-testid="landing-bbi-image" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {[
                ["Verified Brand Page", "Your story, logo & store links on a Google-indexed page."],
                ["AI SEO Articles", "Keyword-rich guides about your brand, written & published by AI."],
                ["AI Search Ready", "Schema markup + llms.txt so ChatGPT & Gemini can cite you."],
                ["Visibility Reports", "Client login with page views, article views & growth stats."],
              ].map(([t, d]) => (
                <div key={t} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
                  <div className="text-white font-semibold text-sm">{t}</div>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 9 · BLOG PREVIEW ═══════════ */}
      <section className="py-24" data-testid="blog-preview">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
                Learn
              </div>
              <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
                From the blog
              </h2>
            </div>
            <Link to="/blog" className="mkt-btn-ghost text-sm">
              All articles <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {previewPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="mkt-glass mkt-hover-lift overflow-hidden group"
                data-testid={`blog-preview-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-black">
                  <img
                    src={p.cover}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-400 font-bold">
                    {p.date} · {p.readMin} min
                  </div>
                  <h3 className="font-mkt-display text-lg font-semibold tracking-tight mt-2 group-hover:text-indigo-200 transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ FINAL CTA ═══════════ */}
      <section className="pt-12 pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass-strong p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
                Ready to ship?
              </div>
              <h3 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
                Grow your brand with AI.
              </h3>
              <p className="text-slate-400 mt-4 max-w-xl mx-auto">
                Sign up free and ship your first AI listing in minutes — no card needed.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link to="/signup" className="mkt-btn-gradient" data-testid="cta-final-start">
                  Start Free Trial <ArrowUpRight size={15} />
                </Link>
                <a
                  href={QUOTATION_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mkt-btn-gradient inline-flex items-center gap-2"
                  data-testid="cta-final-quotation"
                >
                  Connect for Quotation <ArrowUpRight size={14} />
                </a>
                <a href={QUOTATION_EMAIL} className="mkt-btn-ghost" data-testid="cta-final-email">
                  Email for Quotation
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ MARKETPLACE LANDING LINKS (SEO/GEO internal links) ═══════════ */}
      <section className="py-12 border-t border-white/[0.05]" data-testid="marketplace-seo-links">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold mb-4">
            AI Catalog for every marketplace
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { slug: "myntra", label: "AI Catalog for Myntra" },
              { slug: "ajio", label: "AI Catalog for AJIO" },
              { slug: "nykaa", label: "AI Catalog for Nykaa" },
              { slug: "amazon", label: "AI Catalog for Amazon" },
              { slug: "flipkart", label: "AI Catalog for Flipkart" },
              { slug: "meesho", label: "AI Catalog for Meesho" },
            ].map((m) => (
              <Link
                key={m.slug}
                to={`/ai-catalog-${m.slug}`}
                data-testid={`landing-marketplace-link-${m.slug}`}
                className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white/75 hover:bg-white/[0.08] hover:text-white transition"
              >
                {m.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </MarketingShell>
  );
}
